<script>
import BaseView from "@/mixins/BaseView.vue";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin.ts";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import OmniForm from "@/components/omni/OmniForm.vue";
import FormActions from "@/components/form/FormActions.vue";
import OrderEditInfo from "@/views/orders/edit/components/OrderEditInfo.vue";
import OrderEditFiles from "@/views/orders/edit/components/OrderEditFiles.vue";
import OrderEditStops from "@/views/orders/edit/components/OrderEditStops.vue";
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import OrderStatus from "@/components/statuses/OrderStatus.vue";
import InfoMessage from "@/components/messages/InfoMessage.vue";
import { mapActions, mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import { useSnackbarStore } from "@/store/snackbar.store.ts";
import { OrderStopStatus, OrderStopType } from "@/constants/order.ts";
import { useDateTime } from "@/composables/index.ts";
import OrderValidator from "@/validations/order.validator.ts";
import { generateId } from "@/utils/generateId.ts";

const { dateTZ } = useDateTime();

export default {
  name: "OrderEdit",
  components: {
    InfoMessage,
    OrderStatus,
    DispatcherSelect,
    OrderEditStops,
    OrderEditFiles,
    OrderEditInfo,
    FormActions,
    OmniForm,
  },
  mixins: [BaseView, OrdersMixin, ValidationMixin],
  data() {
    return {
      originalOrder: null,
      loading: false,
      order: {
        freights: [],
        orderStops: [],
        refNumbers: [],
        sendUpdatesTo: [],
        rate: "",
        underTracking: false,
        teamForBroker: false,
        protectFromFreezing: false,
        requiredDriverCertificates: [],
        hazmat: false,
        temperatures: [],
        $hasTemperatureControl: false,
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher", "dispatchers"]),
    createdAt() {
      return this.dispatcher ? dateTZ(this.order.createdAt, this.dispatcher.timezone) : "";
    },
    isCreateNew() {
      return this.$route.params.id === undefined;
    },
  },
  watch: {
    dispatcher: {
      handler(value) {
        if (this.isCreateNew && value) {
          this.order.createdBy = this.dispatcher.id;
        }
      },
      immediate: true,
    },
    "order.$hasTemperatureControl"(value) {
      if (!value) {
        this.order.temperatures = [];
        this.order.protectFromFreezing = false;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError", "showSnackbarSuccess"]),
    async load() {
      if (this.isCreateNew) {
        this.setTitle("New Order", "Orders");

        // add empty stops
        await this.addStop(this.STOP_PICK_UP);
        await this.addStop(this.STOP_DELIVERY);

        this.updateDeliveryFreights();

        return;
      }

      this.loading = true;
      const response = await this.$api.orders.findOrderById(this.$route.params.id);
      if (response.success) {
        this.originalOrder = JSON.parse(JSON.stringify(response.data));
        this.order = response.data;
        if ((this.order.temperatures && this.order.temperatures.length === 2) || this.order.protectFromFreezing) {
          this.order.$hasTemperatureControl = true;
        }
      } else {
        this.showSnackbarError("Error loading order data");
      }

      this.loading = false;
    },
    async addFreight(stop) {
      const id = generateId();
      const freight = {
        id: id,
        type: "pl",
        quantity: 1,
        weight: "",
        stackable: false,
      };

      this.order.freights.push(freight);
      stop.freights.push(freight.id);
    },
    async addStop(stopType) {
      const stop = {
        tempId: generateId(),
        type: stopType,
        status: OrderStopStatus.notStarted,
        freights: [],
        note: "",
        timeType: "f",
        timeFrom: null,
        timeTo: null,
        time2Type: "f",
        time2From: null,
        time2To: null,
      };
      if (stopType === this.STOP_PICK_UP) await this.addFreight(stop);
      this.order.orderStops.push({ ...stop, position: this.order.orderStops.length + 1 });
    },
    async save() {
      const { valid } = await this.$refs.form.validate();
      const isOrderValid = new OrderValidator(this.order).validate();
      if (!valid || !isOrderValid) {
        this.scrollToError();
        return;
      }

      delete this.order.polyline;
      this.isSubmitting = true;
      const orderForApi = {
        ...this.order,
        orderStops: this.order.orderStops.map(({ tempId, ...rest }) => rest),
      };
      const response = await this.$api.orders.upsertOrder(orderForApi);

      if (response.success) {
        await this.$refs.filesForm.saveFiles(response.data.id);
        this.showSnackbarSuccess(this.$t("general.saved"));
        if (this.order.tripId) {
          this.$router.push({ name: "trip-edit", params: { id: this.order.tripId } });
        } else {
          this.$router.push({ name: "order-view", params: { id: response.data.id } });
        }
      } else {
        this.showSnackbarError(this.$t("general.save-error"));
      }

      this.isSubmitting = false;
    },
    goBack() {
      this.$router.back();
    },
    updateDeliveryFreights() {
      const allOrderFreightIds = this.order.freights.flatMap((freight) => freight.id);
      const hasOnlyOneUnstartedDelivery =
        this.order.orderStops.filter(
          (orderStop) => orderStop.type === OrderStopType.delivery && orderStop.status === OrderStopStatus.notStarted,
        ).length === 1;

      this.order = {
        ...this.order,
        orderStops: this.order.orderStops.map((orderStop) => {
          const isDelivery = orderStop.type === OrderStopType.delivery;
          const isDeleted = orderStop.isDeleted;
          const hasProcessStarted = orderStop.status !== OrderStopStatus.notStarted;

          if (!isDelivery || isDeleted || hasProcessStarted) {
            return orderStop;
          }

          if (hasOnlyOneUnstartedDelivery) {
            return {
              ...orderStop,
              freights: allOrderFreightIds,
            };
          }

          return {
            ...orderStop,
            freights: orderStop.freights.filter((freight) => allOrderFreightIds.includes(freight)),
          };
        }),
      };
    },
  },
};
</script>
<template>
  <omni-form ref="form">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
      <div v-if="order.id" class="d-flex align-center">
        <h3 class="title font-weight-medium">Order #{{ order.number }}</h3>
        <order-status v-if="order.status" :order="order" class="ml-4"></order-status>
        <info-message v-if="order.tripId" class="ml-6">
          After changing order information, you will be automatically redirected to the Trip manage page to synchronize
          information
        </info-message>
      </div>
      <h3 v-else class="title font-weight-medium">Create new order</h3>
      <div class="d-flex align-center flex-wrap">
        <div class="d-flex align-center mr-5">
          <span class="text-grey-darken-1 mr-2 mb-1">Created date:</span>
          <span class="text-grey-darken-3">{{ createdAt }}</span>
        </div>
        <dispatcher-select v-model="order.createdBy" :disabled="true" data-qa="created_by" label="Created by" />
      </div>
    </div>

    <order-edit-info v-model="order"></order-edit-info>
    <order-edit-stops
      v-model="order"
      :original-order="originalOrder"
      @update-delivery-freights="updateDeliveryFreights"
    ></order-edit-stops>
    <order-edit-files v-if="!loading" ref="filesForm" :order="order"></order-edit-files>
    <form-actions :loading="isSubmitting" :order="order" @cancel="goBack" @submit="save"></form-actions>
  </omni-form>
</template>

<style lang="scss">
.v-application--is-ltr .create-order .v-text-field .v-input__prepend-inner {
  padding-right: 8px;
}

.v-input.v-input--is-label-active {
  &.text-orange {
    .theme--light.v-label {
      color: #fb8c00;
    }
  }

  &.text-green {
    .theme--light.v-label {
      color: #4caf50;
    }
  }
}
</style>
